import { Button, Typography } from '@mui/material';
import type { ReactNode } from 'react';

import { cn } from '@/shared/lib/cn';

export type ListStateTone = 'success' | 'neutral' | 'error';

/** The ring is the same hue at a fraction of the opacity, so the icon sits in a
 *  soft halo rather than a hard disc — the dock's way of giving a mark presence
 *  without an outline. */
const TONE_CLASSES: Record<ListStateTone, string> = {
  success: 'bg-success/12 text-success ring-success/8',
  neutral: 'bg-surface-subtle text-content-muted ring-surface-subtle/60',
  error: 'bg-error/12 text-error ring-error/8',
};

interface ListStateProps {
  icon: ReactNode;
  title: string;
  body: string;
  /** Errors announce themselves; an empty list is not an emergency. */
  tone?: ListStateTone;
  actionLabel?: string;
  onAction?: () => void;
  /** Drops the border and surface for a state rendered inside a panel that
   *  already draws them — otherwise it reads as a box inside a box. */
  bare?: boolean;
  className?: string;
}

/**
 * The empty, error and no-results state every list in the app renders.
 *
 * Shared because the distinction these states carry is easy to lose: the legacy
 * portal let its data tables print "There are no records to display" for a
 * successful empty response AND for a failed request, so a director could not
 * tell "you're done" from "this is broken". Making the three states one
 * component with three tones is what keeps them distinguishable everywhere.
 */
export const ListState = ({
  icon,
  title,
  body,
  tone = 'neutral',
  actionLabel,
  onAction,
  bare = false,
  className,
}: ListStateProps) => (
  <div
    role={tone === 'error' ? 'alert' : 'status'}
    className={cn(
      'flex flex-col items-center gap-3 px-6 py-10 text-center',
      !bare && 'rounded-3xl border border-divider bg-surface shadow-card',
      className,
    )}
  >
    <span
      className={cn(
        'flex size-14 items-center justify-center rounded-full ring-8 [&>svg]:text-[28px]',
        TONE_CLASSES[tone],
      )}
    >
      {icon}
    </span>

    <div>
      <Typography variant="h3" component="p" className="text-base font-bold">
        {title}
      </Typography>
      <Typography variant="body2" color="text.secondary" className="mt-1 max-w-sm">
        {body}
      </Typography>
    </div>

    {actionLabel !== undefined && onAction !== undefined && (
      <Button variant={tone === 'error' ? 'contained' : 'outlined'} onClick={onAction}>
        {actionLabel}
      </Button>
    )}
  </div>
);
